'use client'

// Constants
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { EXPERIMENTS } from '@src/experiments'
import { PreorderContext } from '@src/quince-site-common/src/contexts/preorder-context/preorder.context'
import { useRefreshedProduct, withProductContextProvider } from '@src/quince-site-common/src/contexts/product-context'
import ContentfulPreOrderModal from '@src/quince-site-common/src/domains/contentful/ContentfulPreOrder'
import Product from '@src/quince-site-common/src/domains/Product'
import ProductCard from '@src/quince-site-common/src/domains/ProductCard'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import useExperimentBucket from '@src/quince-site-common/src/hooks/useExperimentBucket'
import PageBuilder from '@src/quince-site-common/src/organisms/pageBuilder'

import ProductPageContextProvider from '../../contexts/ProductPageContextProvider'
import ProductPageContext from '../../domains/ProductPageContext'
import { TPDPSectionData } from '../../types/pdp-section-data.type'
import { ProductSectionType } from '../../types/productPage.types'

import { CUSTOM_SECTION_TYPES_VS_RENDERER, SECTION_TYPES_VS_RENDERER } from './productPage.constants'
import { getRefreshedProductOptions, useUpdatedContainerConfig } from './productPage.utils'

// @i18n-ignore
export const SHOW_FULL_PDP_KEY = '_showFullPDP'

const useContainerRenderer = (
  product: Product,
  sectionData: TPDPSectionData,
  recommendedContentfulProductIds: string[] | undefined,
  recommendedProductCards?: ProductCard[],
  preOrderModalData?: ContentfulPreOrderModal,
) => {
  return useCallback(
    (props: {
      sectionType: ProductSectionType
      customSectionType: ProductSectionType
      mteId?: string
      isMiniSimilarProduct?: boolean
    }) => {
      const sectionType: ProductSectionType = props.sectionType
      const Section = SECTION_TYPES_VS_RENDERER[sectionType]
      if (!Section) {
        if (sectionType === ProductSectionType.CUSTOM_SECTION) {
          const customSectionType: ProductSectionType = props.customSectionType
          const CustomSection = CUSTOM_SECTION_TYPES_VS_RENDERER[customSectionType]
          if (!CustomSection) {
            return <></>
          }
          return (
            <CustomSection
              product={product}
              recommendedContentfulProductIds={recommendedContentfulProductIds}
              recommendedProductCards={recommendedProductCards}
              preOrderModalData={preOrderModalData}
              mteId={props.mteId}
              isMiniSimilarProduct={props.isMiniSimilarProduct}
              sectionData={sectionData}
            />
          )
        }
        return <></>
      }
      return (
        <Section
          product={product}
          recommendedContentfulProductIds={recommendedContentfulProductIds}
          recommendedProductCards={recommendedProductCards}
          preOrderModalData={preOrderModalData}
          mteId={props.mteId}
          isMiniSimilarProduct={props.isMiniSimilarProduct}
          sectionData={sectionData}
        />
      )
    },
    [product, recommendedContentfulProductIds],
  )
}

const ProductPage = ({ pageData }: { pageData: ProductPageContext }) => {
  const preorderCtx = useContext(PreorderContext)
  const preOrderModalData = preorderCtx.preOrderModalData

  const { product: refreshedBaseProduct, isLoading } = useRefreshedProduct(pageData.product)

  const product: Product = useMemo(() => {
    const defaultProduct = pageData.product
    if (refreshedBaseProduct && !isLoading) {
      const refreshedProductOptions = getRefreshedProductOptions(defaultProduct, refreshedBaseProduct)
      return {
        ...defaultProduct,
        productOptions: refreshedProductOptions,
        images: refreshedBaseProduct.images,
        new_color_options: refreshedBaseProduct.new_color_options,
      }
    }
    return defaultProduct
  }, [pageData.product, refreshedBaseProduct, isLoading])

  const containerRenderer = useContainerRenderer(
    product,
    pageData.sectionData,
    pageData.recommendedContentfulProductIds,
    pageData.recommendedProductCards,
    preOrderModalData,
  )

  const deviceInfo = useDeviceInfo()
  const isMobile = deviceInfo.deviceType === DEVICE_TYPES.MOBILE

  const miniPDPBucket = useExperimentBucket(EXPERIMENTS.MINI_PDP.experimentName)

  const updateShowFullPDP = () => {
    const miniPDPInTestA = miniPDPBucket === EXPERIMENTS.MINI_PDP.bucketNames.testA
    if (typeof window === 'undefined' || (miniPDPInTestA && isMobile)) return true

    const sessionValue = sessionStorage.getItem(SHOW_FULL_PDP_KEY)
    if (sessionValue) return sessionValue === 'true'
    sessionStorage.setItem(SHOW_FULL_PDP_KEY, 'false')
    return false
  }

  const [showFullPDP, setShowFullPDP] = useState<boolean>(() => updateShowFullPDP())

  const updateShowFullPDPInSession = useCallback((val: boolean) => {
    sessionStorage.setItem(SHOW_FULL_PDP_KEY, `${val}`)
    setShowFullPDP(val)
  }, [])

  useEffect(() => {
    setShowFullPDP(updateShowFullPDP())
  }, [miniPDPBucket])

  const { mobileContainerConfig, desktopContainerConfig } = useUpdatedContainerConfig(pageData, showFullPDP)

  const updatePageDataContext: ProductPageContext = useMemo(
    () => ({
      product,
      sectionData: pageData.sectionData,
      mobileContainerConfig: mobileContainerConfig,
      desktopContainerConfig: desktopContainerConfig,
      recommendedContentfulProductIds: pageData.recommendedContentfulProductIds,
    }),
    [pageData, product, mobileContainerConfig, desktopContainerConfig],
  )

  return (
    <ProductPageContextProvider showFullPDP={showFullPDP} updateShowFullPDPInSession={updateShowFullPDPInSession}>
      <PageBuilder
        // ref={pageBuilderRef}
        context={updatePageDataContext}
        selfContainerRenderer={containerRenderer}
        className={'clip-horizontal-scroll'}
        isMobile={isMobile}
      />
    </ProductPageContextProvider>
  )
}

export default withProductContextProvider(ProductPage)
